// Graphs — Basic vs Optimized code for every "Beginner Problems" drill listed in the Graphs
// Explanation modal. Same shape as backtrackingDrills.js: concise steps + an interactive
// "watch it happen" visualizer per version, reusing ApproachVisualizer's graph-rendering
// support. Three drills cross-reference the curated problems instead of duplicating content.

const graphDrills = [
  {
    id: 'build-adjacency-list',
    title: 'Represent a Graph: Adjacency Matrix vs. Adjacency List',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Adjacency Matrix',
        explanation: 'Use a V×V grid of booleans (or 0s and 1s): matrix[i][j] = true means an edge exists between i and j. Simple, and checking whether a specific edge exists is instant — but the grid always costs V² space, even for a graph with very few edges.',
        steps: [
          'Allocate a V×V boolean grid, all false initially.',
          'For each edge (a, b), set matrix[a][b] = true (and matrix[b][a] = true, for an undirected graph).',
          'To check if an edge exists, look up matrix[a][b] directly — O(1).',
          'To list a node\'s neighbors, scan its entire row — O(V), even if it only has 1 neighbor.',
        ],
        code:
`boolean[][] buildMatrix(int v, int[][] edges) {
    boolean[][] matrix = new boolean[v][v];
    for (int[] edge : edges) {
        matrix[edge[0]][edge[1]] = true;
        matrix[edge[1]][edge[0]] = true;   // omit for a directed graph
    }
    return matrix;
}`,
        complexity: { time: 'O(1) edge lookup, O(V) to list neighbors', space: 'O(V²) — always, regardless of edge count' },
      },
      {
        name: 'Optimized — Adjacency List',
        explanation: 'For each node, keep just a list of its actual neighbors — nothing is stored for pairs that AREN\'T connected. Space scales with how many edges actually exist, not the square of the node count, which matters enormously for sparse graphs (most real-world graphs).',
        steps: [
          'Keep a map (or array) from each node to a list of its neighbors.',
          'For each edge (a, b), append b to a\'s list (and a to b\'s list, for undirected).',
          'To list a node\'s neighbors, read its list directly — costs exactly as much as it has neighbors.',
          'Checking if a SPECIFIC edge exists costs O(degree) — a linear scan of that node\'s list, unlike the matrix\'s O(1).',
        ],
        visual: {
          graph: { label: 'graph', nodes: [0, 1, 2, 3], edges: [[0, 1], [0, 2], [1, 2], [2, 3]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [0, 1] }, highlightEdges: [[0, 1]], note: 'Edge (0,1): append 1 to node 0\'s list, and 0 to node 1\'s list.' },
            { highlights: { graph: [0, 2] }, highlightEdges: [[0, 2]], note: 'Edge (0,2): append 2 to node 0\'s list, and 0 to node 2\'s list. Node 0\'s list is now [1, 2].' },
            { highlights: { graph: [2, 3] }, highlightEdges: [[2, 3]], note: 'Edge (2,3): append 3 to node 2\'s list, and 2 to node 3\'s list. Final: 0→[1,2], 1→[0,2], 2→[0,1,3], 3→[2] — total space proportional to 8 (2× the 4 edges), not 16 (4²).' },
          ],
        },
        code:
`Map<Integer, List<Integer>> buildAdjacencyList(int v, int[][] edges) {
    Map<Integer, List<Integer>> adj = new HashMap<>();
    for (int i = 0; i < v; i++) adj.put(i, new ArrayList<>());
    for (int[] edge : edges) {
        adj.get(edge[0]).add(edge[1]);
        adj.get(edge[1]).add(edge[0]);   // omit for a directed graph
    }
    return adj;
}`,
        complexity: { time: 'O(degree) edge lookup, O(degree) to list neighbors', space: 'O(V + E) — scales with actual edges, not V²' },
      },
    ],
  },
  {
    id: 'graph-bfs-traversal',
    title: 'Breadth-First Search Traversal',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Linear Scan for "Already Visited"',
        explanation: 'Keep visited nodes in a plain List, and check membership with .contains() before visiting a new one. Correct, but .contains() on a List scans every entry already in it — the visited check gets slower as more nodes are visited.',
        steps: [
          'Keep a List<Integer> of visited nodes and a queue, starting with the source.',
          'While the queue isn\'t empty, dequeue a node and process it.',
          'For each neighbor, check visited.contains(neighbor) — an O(n) scan.',
          'If not visited, add it to both the visited list and the queue.',
        ],
        code:
`void bfsBasic(int start, Map<Integer, List<Integer>> adj) {
    List<Integer> visited = new ArrayList<>();
    Queue<Integer> queue = new LinkedList<>();
    visited.add(start);
    queue.add(start);

    while (!queue.isEmpty()) {
        int node = queue.poll();
        for (int neighbor : adj.get(node)) {
            if (!visited.contains(neighbor)) {   // O(n) scan every time
                visited.add(neighbor);
                queue.add(neighbor);
            }
        }
    }
}`,
        complexity: { time: 'O(V² + E) — the visited check dominates', space: 'O(V)' },
      },
      {
        name: 'Optimized — HashSet for "Already Visited"',
        explanation: 'Use a HashSet instead of a List for tracking visited nodes — membership checking (and adding) both become O(1) average, regardless of how many nodes have already been visited.',
        steps: [
          'Keep a HashSet<Integer> of visited nodes and a queue, starting with the source.',
          'While the queue isn\'t empty, dequeue a node and process it.',
          'For each neighbor, check visited.contains(neighbor) — O(1) average with a HashSet.',
          'If not visited, add it (visited.add() also doubles as the check, via its boolean return).',
        ],
        code:
`void bfs(int start, Map<Integer, List<Integer>> adj) {
    Set<Integer> visited = new HashSet<>();
    Queue<Integer> queue = new LinkedList<>();
    visited.add(start);
    queue.add(start);

    while (!queue.isEmpty()) {
        int node = queue.poll();
        for (int neighbor : adj.get(node)) {
            if (visited.add(neighbor)) {   // O(1) average — true if newly added
                queue.add(neighbor);
            }
        }
    }
}`,
        complexity: { time: 'O(V + E)', space: 'O(V)' },
        interviewNote: "Try the \"BFS Traversal\" mode in this pattern's interactive playground to watch this exact algorithm explore a graph level by level, from any starting node.",
      },
    ],
  },
  {
    id: 'graph-dfs-traversal',
    title: 'Depth-First Search Traversal',
    level: 'Easy',
    versions: [
      {
        name: 'Basic — Recursive',
        explanation: 'Visit a node, then recursively visit each unvisited neighbor. Reads cleanly and mirrors the definition of DFS directly — but each recursive call adds a frame to Java\'s own call stack, the same trade-off covered in the Stack pattern.',
        steps: [
          'Mark the current node visited, and process it.',
          'For each neighbor, if it hasn\'t been visited, recurse into it.',
          'The recursion naturally dives as deep as possible before backtracking.',
          'A very large or deeply-connected graph risks a StackOverflowError.',
        ],
        code:
`void dfsBasic(int node, Set<Integer> visited, Map<Integer, List<Integer>> adj) {
    visited.add(node);
    for (int neighbor : adj.get(node)) {
        if (!visited.contains(neighbor)) {
            dfsBasic(neighbor, visited, adj);
        }
    }
}`,
        complexity: { time: 'O(V + E)', space: 'O(V) — call-stack depth, up to the whole graph in the worst case' },
      },
      {
        name: 'Optimized — Iterative With an Explicit Stack',
        explanation: 'Push nodes onto your OWN stack instead of relying on Java\'s call stack — the exact same swap covered for tree traversal in the Stack and Trees patterns. No recursion, no risk of overflowing on a huge or deeply-nested graph.',
        steps: [
          'Push the start node onto a stack.',
          'While the stack isn\'t empty, pop a node. If it hasn\'t been visited, mark it visited and process it.',
          'Push all of its neighbors onto the stack (unvisited ones will be handled when popped).',
          'The stack naturally replicates the same depth-first order, entirely off the call stack.',
        ],
        visual: {
          graph: { label: 'graph', nodes: [0, 1, 2, 3], edges: [[0, 1], [0, 2], [1, 3]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [0] }, note: 'Pop 0 (start). Mark visited. Push neighbors 1 and 2.' },
            { highlights: { graph: [2] }, visited: [0], note: 'Pop 2 (last pushed). Mark visited. Push its unvisited neighbors (0 is already visited, skip).' },
            { highlights: { graph: [1] }, visited: [0, 2], note: 'Pop 1. Mark visited. Push its unvisited neighbor, 3.' },
            { highlights: { graph: [3] }, visited: [0, 1, 2], note: 'Pop 3. Mark visited. No unvisited neighbors. Stack empty — DFS complete: 0, 2, 1, 3.' },
          ],
        },
        code:
`void dfs(int start, Map<Integer, List<Integer>> adj) {
    Set<Integer> visited = new HashSet<>();
    Deque<Integer> stack = new ArrayDeque<>();
    stack.push(start);

    while (!stack.isEmpty()) {
        int node = stack.pop();
        if (visited.contains(node)) continue;
        visited.add(node);
        // process node here
        for (int neighbor : adj.get(node)) {
            if (!visited.contains(neighbor)) {
                stack.push(neighbor);
            }
        }
    }
}`,
        complexity: { time: 'O(V + E)', space: 'O(V) — your own explicit stack instead of Java\'s' },
        interviewNote: "Try the \"DFS Traversal\" mode in this pattern's interactive playground to compare its \"dive deep first\" order directly against BFS's level-by-level spread, on the same graph.",
      },
    ],
  },
  {
    id: 'number-of-islands-drill',
    title: 'Number of Islands',
    level: 'Medium',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Number of Islands\" there for the complete step-by-step and both code versions.",
  },
  {
    id: 'detect-cycle-via-union-find',
    title: 'Detect a Cycle Using Union-Find',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Union-Find Without Path Compression',
        explanation: 'Track which "component" (connected group) each node belongs to using a parent-pointer array. For each edge, if both endpoints already point to the same root, adding this edge would close a cycle. Correct, but without any shortcutting, find() can walk a long chain of parent pointers every single call.',
        steps: [
          'Start with every node as its own parent (its own separate component).',
          'For each edge (a, b), find the root of a and the root of b by following parent pointers.',
          'If both roots are the same, this edge connects two nodes already in the same component — a cycle!',
          'Otherwise, union them: point one root at the other, merging the two components.',
        ],
        code:
`int[] parent;

boolean hasCycleBasic(int n, int[][] edges) {
    parent = new int[n + 1];
    for (int i = 0; i <= n; i++) parent[i] = i;

    for (int[] edge : edges) {
        int rootA = find(edge[0]);
        int rootB = find(edge[1]);
        if (rootA == rootB) return true;   // already connected — cycle
        parent[rootA] = rootB;
    }
    return false;
}

private int find(int x) {
    while (parent[x] != x) x = parent[x];   // no shortcutting — can be a long chain
    return x;
}`,
        complexity: { time: 'O(n) per find in the worst case — a long chain — O(n²) total across all edges', space: 'O(n)' },
      },
      {
        name: 'Optimized — Path Compression',
        explanation: 'Every time find() walks a chain to reach the root, point every node along that path DIRECTLY at the root before returning — flattening the chain permanently. Future find() calls on any of those nodes become nearly instant.',
        steps: [
          'Same union-find structure, but find() is rewritten to flatten the path.',
          'Walk to the root as before, then make a second pass (or use recursion) re-pointing every visited node straight at the root.',
          'Future calls to find() on any of those nodes now take O(1) — no chain to walk.',
          'Combined with union by rank (attaching the smaller tree under the bigger one), this gets find()/union() to near O(1) amortized.',
        ],
        visual: {
          graph: { label: 'graph', nodes: [1, 2, 3], edges: [[1, 2], [1, 3]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [1, 2] }, highlightEdges: [[1, 2]], note: 'Edge (1,2): find(1)=1, find(2)=2 — different roots. Union: parent[2] = 1.' },
            { highlights: { graph: [1, 3] }, highlightEdges: [[1, 3]], note: 'Edge (1,3): find(1)=1, find(3)=3 — different roots. Union: parent[3] = 1.' },
            { highlights: { graph: [2, 3] }, highlightEdges: [[2, 3]], note: 'Edge (2,3): find(2)=1, find(3)=1 — SAME root! This edge would close a cycle (1-2-3-1). Detected.' },
          ],
        },
        code:
`int[] parent;

boolean hasCycle(int n, int[][] edges) {
    parent = new int[n + 1];
    for (int i = 0; i <= n; i++) parent[i] = i;

    for (int[] edge : edges) {
        int rootA = find(edge[0]);
        int rootB = find(edge[1]);
        if (rootA == rootB) return true;
        parent[rootA] = rootB;
    }
    return false;
}

private int find(int x) {
    if (parent[x] != x) {
        parent[x] = find(parent[x]);   // path compression — flatten on the way back up
    }
    return parent[x];
}`,
        complexity: { time: 'O(α(n)) amortized per operation — effectively constant', space: 'O(n)' },
        interviewNote: "α(n) is the inverse Ackermann function — it grows so slowly that for any input size that could ever exist in practice, it's ≤ 4, which is why this is described as \"effectively constant time.\"",
        whenToUse: "This is the foundational technique behind Redundant Connection — worth mastering path compression here first, since the curated problem builds directly on this exact mechanism.",
      },
    ],
  },
  {
    id: 'bipartite-graph-check',
    title: 'Check if a Graph Is Bipartite',
    level: 'Medium',
    versions: [
      {
        name: 'Basic — Try Every Possible 2-Coloring',
        explanation: 'A graph is bipartite if its nodes can be split into two groups such that every edge connects nodes from DIFFERENT groups. Try every possible way of assigning each node to one of two colors, and check whether any assignment satisfies every edge. Correct, but there are 2ⁿ possible colorings to try.',
        steps: [
          'Generate every possible assignment of each node to color A or color B (2ⁿ total).',
          'For each assignment, check every edge — does it connect two different colors?',
          'If any assignment satisfies every edge, the graph is bipartite.',
          'Every one of the 2ⁿ colorings gets generated, even though most fail immediately on the first edge checked.',
        ],
        code:
`boolean isBipartiteBruteForce(int n, Map<Integer, List<Integer>> adj) {
    for (int mask = 0; mask < (1 << n); mask++) {
        if (isValidColoring(mask, n, adj)) return true;
    }
    return false;
}

private boolean isValidColoring(int mask, int n, Map<Integer, List<Integer>> adj) {
    for (int node = 0; node < n; node++) {
        boolean nodeColor = ((mask >> node) & 1) == 1;
        for (int neighbor : adj.get(node)) {
            boolean neighborColor = ((mask >> neighbor) & 1) == 1;
            if (nodeColor == neighborColor) return false;
        }
    }
    return true;
}`,
        complexity: { time: 'O(2ⁿ · (V + E))', space: 'O(1) extra' },
      },
      {
        name: 'Optimized — BFS/DFS Greedy 2-Coloring',
        explanation: 'Color the starting node arbitrarily, then color every neighbor the OPPOSITE color, spreading outward. The moment an edge is found connecting two same-colored nodes, the graph definitively isn\'t bipartite — no need to try any other coloring, since this greedy assignment is forced by the graph\'s structure once the first node\'s color is picked.',
        steps: [
          'Color the starting node (arbitrarily) color A.',
          'BFS or DFS outward: color every neighbor of an A-colored node with B, and vice versa.',
          'If a neighbor is already colored and it MATCHES the current node\'s color, that\'s a conflict — not bipartite.',
          'If the traversal finishes with no conflicts (checking every connected component), the graph is bipartite.',
        ],
        visual: {
          graph: { label: 'graph (4-cycle)', nodes: [0, 1, 2, 3], edges: [[0, 1], [1, 2], [2, 3], [3, 0]], directed: false },
          varNames: [],
          steps: [
            { highlights: { graph: [0] }, note: 'Color node 0: A.' },
            { highlights: { graph: [1] }, note: 'Neighbor 1: color B (opposite of 0).' },
            { highlights: { graph: [2] }, note: 'Neighbor 2 (of 1): color A (opposite of B).' },
            { highlights: { graph: [3] }, note: 'Neighbor 3 (of 2): color B. Check edge (3,0): 3 is B, 0 is A — different, no conflict. Every edge checks out — bipartite!' },
          ],
        },
        code:
`boolean isBipartite(int n, Map<Integer, List<Integer>> adj) {
    int[] color = new int[n];
    Arrays.fill(color, -1);   // -1 = uncolored

    for (int start = 0; start < n; start++) {
        if (color[start] != -1) continue;   // already colored via another component
        color[start] = 0;
        Queue<Integer> queue = new LinkedList<>();
        queue.add(start);

        while (!queue.isEmpty()) {
            int node = queue.poll();
            for (int neighbor : adj.get(node)) {
                if (color[neighbor] == -1) {
                    color[neighbor] = 1 - color[node];   // opposite color
                    queue.add(neighbor);
                } else if (color[neighbor] == color[node]) {
                    return false;   // conflict — same color on both ends of an edge
                }
            }
        }
    }
    return true;
}`,
        complexity: { time: 'O(V + E)', space: 'O(V)' },
        whenToUse: "This greedy 2-coloring is the standard technique any time a problem asks about splitting a graph into two mutually-exclusive groups with a no-same-group-edges rule.",
      },
    ],
  },
  {
    id: 'course-schedule-drill',
    title: 'Course Schedule (Cycle Detection in a Directed Graph)',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Course Schedule\" there for the complete step-by-step and both code versions.",
  },
  {
    id: 'topological-sort-kahns',
    title: 'Topological Sort via DFS (Postorder + Reverse)',
    level: 'Advanced',
    versions: [
      {
        name: 'Basic — DFS Assuming a Valid DAG (No Cycle Safety)',
        explanation: 'Run a DFS, and record each node the moment its recursive call FINISHES (after all its dependents have already finished) — then reverse that finish order. This is correct for a Directed Acyclic Graph, but blindly assumes there\'s no cycle; if the input secretly has one, this version can recurse forever or produce a meaningless order.',
        steps: [
          'DFS from every unvisited node.',
          'Recurse into all of a node\'s neighbors first.',
          'After all neighbors are fully processed, add the current node to a "finish order" list.',
          'Reverse the finish order at the end — but nothing here detects or handles a cycle if one exists.',
        ],
        code:
`List<Integer> topSortBasic(int n, Map<Integer, List<Integer>> adj) {
    List<Integer> finishOrder = new ArrayList<>();
    Set<Integer> visited = new HashSet<>();
    for (int i = 0; i < n; i++) {
        if (!visited.contains(i)) dfs(i, adj, visited, finishOrder);
    }
    Collections.reverse(finishOrder);
    return finishOrder;
}

private void dfs(int node, Map<Integer, List<Integer>> adj, Set<Integer> visited, List<Integer> finishOrder) {
    visited.add(node);
    for (int neighbor : adj.get(node)) {
        if (!visited.contains(neighbor)) dfs(neighbor, adj, visited, finishOrder);
        // no check here for "currently being visited" — a cycle isn't caught
    }
    finishOrder.add(node);
}`,
        complexity: { time: 'O(V + E) — assuming a valid DAG', space: 'O(V)' },
      },
      {
        name: 'Optimized — Three-State DFS (Detects Cycles Too)',
        explanation: 'Track each node as UNVISITED, VISITING (currently on the current DFS path, not yet finished), or VISITED (fully finished). If DFS ever reaches a node that\'s still VISITING, that\'s a back edge to an ancestor on the current path — a cycle, and topological order is impossible.',
        steps: [
          'Mark a node VISITING when its DFS call begins.',
          'Recurse into neighbors: if a neighbor is VISITING, a cycle exists — the graph has no valid topological order.',
          'If a neighbor is UNVISITED, recurse into it.',
          'After all neighbors finish, mark the node VISITED and add it to the finish order — then reverse at the end, exactly as before.',
        ],
        visual: {
          graph: { label: 'graph (0→1, 0→2, 1→3, 2→3)', nodes: [0, 1, 2, 3], edges: [[0, 1], [0, 2], [1, 3], [2, 3]], directed: true },
          varNames: [],
          steps: [
            { highlights: { graph: [0] }, note: 'Visit 0 (mark VISITING). Recurse into neighbor 1.' },
            { highlights: { graph: [1] }, note: 'Visit 1 (VISITING). Recurse into neighbor 3.' },
            { highlights: { graph: [3] }, note: 'Visit 3 (VISITING). No neighbors — mark VISITED, add to finish order: [3].' },
            { highlights: { graph: [1] }, note: 'Back at 1: no more neighbors — mark VISITED, add to finish order: [3, 1]. Back at 0, recurse into neighbor 2.' },
            { highlights: { graph: [2] }, note: 'Visit 2 (VISITING). Recurse into neighbor 3 — already VISITED (not VISITING), no cycle, just skip. Mark 2 VISITED: [3, 1, 2].' },
            { highlights: { graph: [0] }, note: 'Back at 0: mark VISITED, add to finish order: [3, 1, 2, 0]. Reverse it: [0, 2, 1, 3] — a valid topological order (0 before both 1 and 2; both before 3).' },
          ],
        },
        code:
`private static final int UNVISITED = 0, VISITING = 1, VISITED = 2;

List<Integer> topSort(int n, Map<Integer, List<Integer>> adj) {
    int[] state = new int[n];
    List<Integer> finishOrder = new ArrayList<>();
    for (int i = 0; i < n; i++) {
        if (state[i] == UNVISITED) {
            if (!dfs(i, adj, state, finishOrder)) return new ArrayList<>();   // cycle — no valid order
        }
    }
    Collections.reverse(finishOrder);
    return finishOrder;
}

private boolean dfs(int node, Map<Integer, List<Integer>> adj, int[] state, List<Integer> finishOrder) {
    state[node] = VISITING;
    for (int neighbor : adj.get(node)) {
        if (state[neighbor] == VISITING) return false;   // back edge — cycle!
        if (state[neighbor] == UNVISITED && !dfs(neighbor, adj, state, finishOrder)) return false;
    }
    state[node] = VISITED;
    finishOrder.add(node);
    return true;
}`,
        complexity: { time: 'O(V + E)', space: 'O(V)' },
        interviewNote: "This DFS approach and Kahn's algorithm (BFS with in-degree counting, used in this pattern's Course Schedule problem) both produce valid topological orders in O(V + E) — worth knowing both exist and being able to explain the trade-off: Kahn's naturally reports the cycle-causing nodes as \"never reached zero in-degree,\" while this version detects a cycle via the VISITING-state check.",
        whenToUse: "Reach for this DFS version when a natural recursive traversal is already being written anyway; reach for Kahn's (BFS) when explicit level-by-level processing order matters, or when avoiding recursion depth is a concern.",
      },
    ],
  },
  {
    id: 'redundant-connection-drill',
    title: 'Redundant Connection',
    level: 'Advanced',
    crossReference:
      "This drill's full Basic vs. Optimized walkthrough lives in this pattern's curated problem list — open " +
      "\"Redundant Connection\" there for the complete step-by-step and both code versions. It builds directly " +
      "on the Union-Find cycle detection covered in this pattern's \"Detect a Cycle Using Union-Find\" drill.",
  },
];

export default graphDrills;
